import React, { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Pencil, Trash2, Plus, RefreshCw, MessageSquarePlus, Check, X, ChevronDown, ChevronRight, ShieldAlert, ShieldCheck, BookmarkPlus } from "lucide-react";
import * as api from "../../lib/api";

const STATUS_META = {
  active: { label: "Active", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  met: { label: "Met", cls: "bg-green-100 text-green-800 border-green-200" },
  rolled_over: { label: "Rolled Over", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  draft: { label: "Draft", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  archived: { label: "Archived", cls: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

const REC_LABEL = { roll_over: "Roll Over", modify: "Modify", replace: "Replace" };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
};

const sortedLog = (log) =>
  [...(log || [])].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "") || (b.created_at || "").localeCompare(a.created_at || ""));

export function GoalCard({ goal, periods = [], onChanged, showStudent = false, iepDueDate = "", collapsed: controlledCollapsed, onToggleCollapsed }) {
  const [editingGoal, setEditingGoal] = useState(false);
  const [gEdit, setGEdit] = useState({});
  const [editObj, setEditObj] = useState(null); // objective id being edited
  const [objDraft, setObjDraft] = useState({});
  const [addingObj, setAddingObj] = useState(false);
  const [newObj, setNewObj] = useState({ description: "", measurement: "", progress_status: "" });
  const [noteFor, setNoteFor] = useState(null); // objective id for note entry
  const [noteText, setNoteText] = useState("");
  const [noteDate, setNoteDate] = useState("");
  const [noteApplyAll, setNoteApplyAll] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // { objId, noteId }
  const [noteEditDraft, setNoteEditDraft] = useState({ text: "", date: "" });
  const [confirmDelGoal, setConfirmDelGoal] = useState(false);
  const [confirmRoll, setConfirmRoll] = useState(false);
  const [confirmDelObj, setConfirmDelObj] = useState(null); // objective id pending delete
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  const toggleCollapsed = onToggleCollapsed || (() => setInternalCollapsed((c) => !c));
  const [goalNote, setGoalNote] = useState(null); // {text,date} when adding a goal-level note
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [comparePeriodIds, setComparePeriodIds] = useState(() => new Set()); // empty = show all notes
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  const status = STATUS_META[goal.status] || STATUS_META.active;
  const period = periods.find((p) => p.id === goal.reporting_period_id);
  const unofficial = !!goal.is_unofficial;

  // IEP meeting approaching → prompt for a recommendation (within 60 days or overdue).
  const iepDaysAway = (() => {
    if (!iepDueDate) return null;
    const d = new Date(iepDueDate + "T00:00:00");
    if (isNaN(d)) return null;
    return Math.round((d - new Date(new Date().toDateString())) / 86400000);
  })();
  const iepDueSoon = iepDaysAway !== null && iepDaysAway <= 60;

  const refresh = () => onChanged && onChanged();

  // Notes must live at exactly one level. A note added at the goal level belongs
  // ONLY to the goal; a note added to an objective belongs ONLY to that objective.
  // Guard against any legacy record where the same entry was stored in both arrays
  // by hiding, from an objective, any note whose id already exists at the goal level.
  const goalNoteIds = new Set((goal.progress_log || []).map((e) => e.id).filter(Boolean));
  const objectiveLog = (o) => (o.progress_log || []).filter((e) => !e.id || !goalNoteIds.has(e.id));

  // Multi-period comparison: map each note to the reporting period whose date window
  // contains its report date, so staff can view notes from several quarters side-by-side.
  const inPeriod = (dateStr, p) => {
    if (!dateStr) return false;
    const s = p.start_date || "";
    const e = p.end_date || "";
    return (!s || dateStr >= s) && (!e || dateStr <= e);
  };
  const comparePeriods = periods
    .filter((p) => comparePeriodIds.has(p.id))
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")); // oldest → newest
  const togglePeriod = (id) => setComparePeriodIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const startEditNote = (objId, e) => { setEditingNote({ objId, noteId: e.id }); setNoteEditDraft({ text: e.text || "", date: e.date || "" }); };
  const saveEditNote = async () => {
    if (!noteEditDraft.text.trim()) { toast.error("Note text required"); return; }
    try {
      await api.updateGoalObjectiveNote(goal.id, editingNote.objId, editingNote.noteId, { text: noteEditDraft.text.trim(), date: noteEditDraft.date || undefined });
      toast.success("Note updated");
      setEditingNote(null); refresh();
    } catch (e) { toast.error("Failed to update note"); }
  };

  const NoteLine = ({ e, objId }) => {
    if (objId && editingNote && editingNote.objId === objId && editingNote.noteId === e.id) {
      return (
        <div className="space-y-1 py-1" data-testid={`note-edit-${e.id}`}>
          <Textarea value={noteEditDraft.text} onChange={(ev) => setNoteEditDraft((p) => ({ ...p, text: ev.target.value }))} className="min-h-[56px]" data-testid={`note-edit-text-${e.id}`} />
          <div className="flex items-center gap-1">
            <Input type="date" value={noteEditDraft.date} onChange={(ev) => setNoteEditDraft((p) => ({ ...p, date: ev.target.value }))} className="h-8 w-36 text-xs" title="Report date" />
            <Button size="sm" onClick={saveEditNote} data-testid={`note-edit-save-${e.id}`}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)}>Cancel</Button>
          </div>
        </div>
      );
    }
    return (
      <div className="group flex items-start gap-1 text-sm text-slate-700 leading-snug">
        <div className="min-w-0 flex-1">
          <span className="font-semibold text-[#00305E]">Report {fmtDate(e.date)}:</span> {e.text}
          {e.author_name && <span className="text-slate-400 text-xs"> — added by {e.author_name}</span>}
          {e.edited_at && <span className="text-slate-400 text-xs italic"> · edited{e.edited_by ? ` by ${e.edited_by}` : ""}</span>}
        </div>
        {objId && (
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#00305E] shrink-0"
            title="Edit note"
            onClick={() => startEditNote(objId, e)}
            data-testid={`note-edit-btn-${e.id}`}
          ><Pencil className="w-3 h-3" /></button>
        )}
      </div>
    );
  };
  // Renders a progress log either flat (default) or grouped by the selected periods.
  const renderNotes = (log, keyPrefix, objId) => {
    const entries = log || [];
    if (comparePeriodIds.size === 0) {
      return sortedLog(entries).map((e) => <NoteLine key={e.id} e={e} objId={objId} />);
    }
    return (
      <div className="space-y-2" data-testid={`notes-grouped-${keyPrefix}`}>
        {comparePeriods.map((p) => {
          const pes = sortedLog(entries.filter((e) => inPeriod(e.date, p)));
          return (
            <div key={p.id} className="rounded border border-slate-100 bg-slate-50 px-2 py-1" data-testid={`notes-period-${keyPrefix}-${p.id}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{p.name}</div>
              {pes.length ? (
                <div className="space-y-1 mt-0.5">{pes.map((e) => <NoteLine key={e.id} e={e} objId={objId} />)}</div>
              ) : (
                <div className="text-xs italic text-slate-400">No notes this period</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const saveGoal = async () => {
    try {
      await api.updateGoal(goal.id, gEdit);
      toast.success("Goal updated");
      setEditingGoal(false);
      refresh();
    } catch (e) { toast.error("Failed to update goal"); }
  };

  const changeStatus = async (v) => {
    if (unofficial && v === "active") { toast.error("Confirm this goal against the official IEP before making it Active"); return; }
    try { await api.updateGoal(goal.id, { status: v }); toast.success("Status updated"); refresh(); }
    catch (e) { toast.error("Failed to update status"); }
  };

  const setRecommendation = async (v) => {
    try { await api.updateGoal(goal.id, { iep_recommendation: v }); toast.success(`Recommendation: ${REC_LABEL[v] || v}`); refresh(); }
    catch (e) { toast.error("Failed to save recommendation"); }
  };

  const draftUnofficial = async () => {
    if (!draftText.trim()) { toast.error("Enter the new goal language"); return; }
    try {
      await api.createGoal({
        student_id: goal.student_id, goal_type: goal.goal_type,
        goal_statement: draftText.trim(),
        reporting_period_id: goal.reporting_period_id || undefined,
        is_unofficial: true, iep_recommendation: goal.iep_recommendation || "modify",
        drafted_from: goal.id,
      });
      toast.success("Draft goal created (Unofficial — confirm against the IEP)");
      setDraftOpen(false); setDraftText(""); refresh();
    } catch (e) { toast.error("Failed to create draft goal"); }
  };

  const confirmOfficial = async () => {
    try { await api.confirmGoalOfficial(goal.id); toast.success("Confirmed against IEP — goal is now Active"); refresh(); }
    catch (e) { toast.error("Failed to confirm"); }
  };

  const doRollover = async () => {
    try { await api.rolloverGoal(goal.id); toast.success("Goal rolled over to next year"); setConfirmRoll(false); refresh(); }
    catch (e) { toast.error("Failed to roll over"); }
  };

  const doDeleteGoal = async () => {
    try { await api.deleteGoal(goal.id); toast.success("Goal deleted"); setConfirmDelGoal(false); refresh(); }
    catch (e) { toast.error("Failed to delete goal"); }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Template name required"); return; }
    try {
      await api.createGoalTemplate({
        name: tplName.trim(),
        goal_type: goal.goal_type || "",
        goal_statement: goal.goal_statement || "",
        objectives: (goal.objectives || []).map((o) => ({ description: o.description || "", measurement: o.measurement || "", progress_status: o.progress_status || "" })),
      });
      toast.success("Saved as template");
      setTplOpen(false); refresh();
    } catch (e) { toast.error("Failed to save template"); }
  };

  const startEditObj = (o) => { setEditObj(o.id); setObjDraft({ description: o.description, measurement: o.measurement, progress_status: o.progress_status }); };
  const saveObj = async (oid) => {
    try { await api.updateGoalObjective(goal.id, oid, objDraft); setEditObj(null); refresh(); }
    catch (e) { toast.error("Failed to save objective"); }
  };
  const delObj = async (oid) => {
    try { await api.deleteGoalObjective(goal.id, oid); setConfirmDelObj(null); refresh(); }
    catch (e) { toast.error("Failed to delete objective"); }
  };
  const addGoalNote = async () => {
    if (!goalNote?.text?.trim()) { toast.error("Note text required"); return; }
    try {
      await api.appendGoalLevelLog(goal.id, { text: goalNote.text.trim(), date: goalNote.date || undefined });
      toast.success("Goal note added");
      setGoalNote(null); refresh();
    } catch (e) { toast.error("Failed to add note"); }
  };
  const addObj = async () => {
    if (!newObj.description.trim()) { toast.error("Objective description required"); return; }
    try { await api.addGoalObjective(goal.id, newObj); setNewObj({ description: "", measurement: "", progress_status: "" }); setAddingObj(false); refresh(); }
    catch (e) { toast.error("Failed to add objective"); }
  };
  const addNote = async (oid) => {
    if (!noteText.trim()) { toast.error("Note text required"); return; }
    try {
      await api.appendGoalLog(goal.id, oid, { text: noteText.trim(), date: noteDate || undefined, apply_to_all: noteApplyAll });
      toast.success(noteApplyAll ? "Note added to all objectives" : "Note added");
      setNoteFor(null); setNoteText(""); setNoteDate(""); setNoteApplyAll(false);
      refresh();
    } catch (e) { toast.error("Failed to add note"); }
  };

  return (
    <Card
      className={`border-l-4 ${unofficial ? "border border-dashed border-amber-400 bg-amber-50/40" : ""}`}
      style={{ borderLeftColor: unofficial ? "#d97706" : "#00305E" }}
      data-testid={`goal-card-${goal.id}`}
    >
      <CardContent className="p-4 space-y-3">
        {unofficial && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-100 border border-amber-300 px-3 py-2" data-testid={`goal-unofficial-banner-${goal.id}`}>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900">
              <ShieldAlert className="w-4 h-4" /> UNOFFICIAL — draft pending confirmation against the official IEP
            </span>
            <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700" onClick={confirmOfficial} data-testid={`goal-confirm-official-${goal.id}`}>
              <ShieldCheck className="w-4 h-4 mr-1" />Confirm against IEP
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={toggleCollapsed} className="text-slate-400 hover:text-[#00305E]" title={collapsed ? "Expand" : "Collapse"} data-testid={`goal-collapse-toggle-${goal.id}`}>
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showStudent && goal.student_name && (
                <span className="font-semibold text-[#00305E]">{goal.student_name}</span>
              )}
              <Badge variant="outline" className="bg-[#00305E]/10 text-[#00305E] border-[#00305E]/20">{goal.goal_type}</Badge>
              {period && <span className="text-xs text-slate-500">{period.name}</span>}
              <span className="text-xs text-slate-400">{goal.school_year}</span>
              {collapsed && (goal.objectives || []).length > 0 && (
                <span className="text-xs text-slate-400">· {(goal.objectives || []).length} objective(s) hidden</span>
              )}
            </div>
            {!editingGoal ? (
              <p className="mt-1 text-sm text-slate-800">{goal.goal_statement || <span className="italic text-slate-400">No goal statement</span>}</p>
            ) : (
              <div className="mt-2 space-y-2">
                <Input defaultValue={goal.goal_type} onChange={(e) => setGEdit((p) => ({ ...p, goal_type: e.target.value }))} placeholder="Goal type" data-testid="goal-edit-type" />
                <Textarea defaultValue={goal.goal_statement} onChange={(e) => setGEdit((p) => ({ ...p, goal_statement: e.target.value }))} placeholder="Overall goal statement" data-testid="goal-edit-statement" />
                <div className="flex flex-wrap gap-2 items-center">
                  <Select defaultValue={goal.reporting_period_id || "none"} onValueChange={(v) => setGEdit((p) => ({ ...p, reporting_period_id: v === "none" ? "" : v }))}>
                    <SelectTrigger className="w-56" data-testid="goal-edit-period"><SelectValue placeholder="Reporting period" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No reporting period</SelectItem>
                      {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea defaultValue={goal.goal_recommendation} onChange={(e) => setGEdit((p) => ({ ...p, goal_recommendation: e.target.value }))} placeholder="Goal Recommendation (added at reporting time)" data-testid="goal-edit-recommendation" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveGoal} data-testid="goal-save-btn"><Check className="w-4 h-4 mr-1" />Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingGoal(false); setGEdit({}); }}><X className="w-4 h-4 mr-1" />Cancel</Button>
                </div>
              </div>
            )}
          </div>
          {!editingGoal && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Select value={goal.status} onValueChange={changeStatus}>
                <SelectTrigger className={`h-8 w-32 text-xs ${status.cls}`} data-testid={`goal-status-${goal.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" disabled={unofficial}>Active</SelectItem>
                  <SelectItem value="met">Met</SelectItem>
                  <SelectItem value="rolled_over">Rolled Over</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  {(goal.status === "draft" || unofficial) && <SelectItem value="draft">Draft</SelectItem>}
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit goal" onClick={() => { setGEdit({}); setEditingGoal(true); }} data-testid={`goal-edit-btn-${goal.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" title="Save as template" onClick={() => { setTplName(goal.goal_type ? `${goal.goal_type} template` : "New template"); setTplOpen(true); }} data-testid={`goal-save-template-${goal.id}`}><BookmarkPlus className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" title="Roll over to next year" onClick={() => setConfirmRoll(true)} data-testid={`goal-rollover-btn-${goal.id}`}><RefreshCw className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Delete goal" onClick={() => setConfirmDelGoal(true)} data-testid={`goal-delete-btn-${goal.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>

        {/* Goal recommendation display */}
        {!editingGoal && goal.goal_recommendation && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">Goal Recommendation{goal.goal_recommendation_date ? ` (${fmtDate(goal.goal_recommendation_date)})` : ""}:</span>{" "}
            {goal.goal_recommendation}
          </div>
        )}

        {/* IEP meeting recommendation prompt */}
        {!editingGoal && !unofficial && (iepDueSoon || goal.iep_recommendation) && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 space-y-2" data-testid={`goal-rec-prompt-${goal.id}`}>
            {iepDueSoon && (
              <p className="text-sm font-medium text-indigo-900">
                IEP meeting {iepDaysAway < 0 ? "was due" : "due"} {new Date(iepDueDate + "T00:00:00").toLocaleDateString()}
                {iepDaysAway >= 0 ? ` (in ${iepDaysAway} day${iepDaysAway === 1 ? "" : "s"})` : " — overdue"}. Choose a recommendation for this goal:
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-600">Recommendation:</span>
              <Select value={goal.iep_recommendation || ""} onValueChange={setRecommendation}>
                <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid={`goal-rec-select-${goal.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="roll_over">Roll Over</SelectItem>
                  <SelectItem value="modify">Modify</SelectItem>
                  <SelectItem value="replace">Replace</SelectItem>
                </SelectContent>
              </Select>
              {goal.iep_recommendation === "roll_over" && (
                <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirmRoll(true)} data-testid={`goal-rec-rollover-${goal.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Roll over now</Button>
              )}
              {(goal.iep_recommendation === "modify" || goal.iep_recommendation === "replace") && !draftOpen && (
                <Button size="sm" variant="outline" className="h-8" onClick={() => setDraftOpen(true)} data-testid={`goal-rec-draft-${goal.id}`}>Draft new language</Button>
              )}
            </div>
            {draftOpen && (
              <div className="space-y-1 pt-1">
                <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)} placeholder="Draft the new goal language (saved as an UNOFFICIAL goal until confirmed against the district IEP)" className="min-h-[60px] bg-white" data-testid={`goal-draft-text-${goal.id}`} />
                <div className="flex gap-1">
                  <Button size="sm" onClick={draftUnofficial} data-testid={`goal-draft-save-${goal.id}`}>Save unofficial draft</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDraftOpen(false); setDraftText(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {!collapsed && (<>
        {/* Compare progress notes across reporting periods */}
        {periods.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`period-compare-${goal.id}`}>
            <span className="text-xs font-semibold text-slate-500">Compare periods:</span>
            <button
              onClick={() => setComparePeriodIds(new Set())}
              className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${comparePeriodIds.size === 0 ? "bg-[#00305E] text-white border-[#00305E]" : "bg-white text-slate-600 border-slate-300 hover:border-[#00305E]"}`}
              data-testid={`period-chip-all-${goal.id}`}
            >All</button>
            {[...periods].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")).map((p) => (
              <button
                key={p.id}
                onClick={() => togglePeriod(p.id)}
                className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${comparePeriodIds.has(p.id) ? "bg-[#00305E] text-white border-[#00305E]" : "bg-white text-slate-600 border-slate-300 hover:border-[#00305E]"}`}
                data-testid={`period-chip-${goal.id}-${p.id}`}
              >{p.name}</button>
            ))}
            {comparePeriodIds.size > 0 && (
              <span className="text-[11px] text-slate-400">showing {comparePeriodIds.size} period(s)</span>
            )}
          </div>
        )}
        {/* Goal-level notes */}
        <div className="rounded-md border border-slate-200 px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">Goal-level notes</span>
            {goalNote === null && (
              <button className="text-xs text-[#00305E] hover:underline inline-flex items-center gap-0.5" onClick={() => setGoalNote({ text: "", date: "" })} data-testid={`add-goal-note-${goal.id}`}><MessageSquarePlus className="w-3 h-3" />Add goal note</button>
            )}
          </div>
          {renderNotes(goal.progress_log, `goal-${goal.id}`)}
          {goalNote !== null && (
            <div className="space-y-1 pt-2">
              <Textarea value={goalNote.text} onChange={(e) => setGoalNote((p) => ({ ...p, text: e.target.value }))} placeholder="Goal-level progress note (append-only)" className="min-h-[56px]" data-testid={`goal-note-text-${goal.id}`} />
              <div className="flex items-center gap-1">
                <Input type="date" value={goalNote.date} onChange={(e) => setGoalNote((p) => ({ ...p, date: e.target.value }))} className="h-8 w-36 text-xs" title="Report date (defaults to today)" />
                <Button size="sm" onClick={addGoalNote} data-testid={`goal-note-save-${goal.id}`}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setGoalNote(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Objectives table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#00305E] hover:bg-[#00305E]">
                <TableHead className="text-white w-[32%]">Objectives</TableHead>
                <TableHead className="text-white w-[13%]">Current Progress</TableHead>
                <TableHead className="text-white w-[15%]">Measurement</TableHead>
                <TableHead className="text-white w-[40%]">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(goal.objectives || []).length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 italic text-sm py-3">No objectives yet.</TableCell></TableRow>
              )}
              {(goal.objectives || []).map((o, idx) => (
                <TableRow key={o.id} className="align-top" data-testid={`objective-row-${o.id}`}>
                  {editObj === o.id ? (
                    <>
                      <TableCell><Textarea value={objDraft.description} onChange={(e) => setObjDraft((p) => ({ ...p, description: e.target.value }))} className="min-h-[60px]" /></TableCell>
                      <TableCell><Input value={objDraft.progress_status} onChange={(e) => setObjDraft((p) => ({ ...p, progress_status: e.target.value }))} placeholder="e.g. Progress Made" /></TableCell>
                      <TableCell><Input value={objDraft.measurement} onChange={(e) => setObjDraft((p) => ({ ...p, measurement: e.target.value }))} placeholder="e.g. Observation" /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => saveObj(o.id)} data-testid={`objective-save-${o.id}`}><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditObj(null)}><X className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-sm">
                        <div className="flex items-start gap-1">
                          <span className="font-semibold text-slate-500">{idx + 1}:</span>
                          <span className="text-slate-800">{o.description || <span className="italic text-slate-400">—</span>}</span>
                        </div>
                        <div className="mt-1 flex gap-2">
                          <button className="text-xs text-slate-400 hover:text-[#00305E] inline-flex items-center gap-0.5" onClick={() => startEditObj(o)} data-testid={`objective-edit-${o.id}`}><Pencil className="w-3 h-3" />Edit</button>
                          <button className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-0.5" onClick={() => setConfirmDelObj(o.id)} data-testid={`objective-delete-${o.id}`}><Trash2 className="w-3 h-3" />Remove</button>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{o.progress_status || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700">{o.measurement || "—"}</TableCell>
                      <TableCell className="text-sm">
                        <div className="space-y-1.5">
                          {renderNotes(objectiveLog(o), `obj-${o.id}`, o.id)}
                          {noteFor === o.id ? (
                            <div className="space-y-1 pt-1">
                              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Progress note" className="min-h-[56px]" data-testid={`note-text-${o.id}`} />
                              {(goal.objectives || []).length > 1 && (
                                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid={`note-apply-all-${o.id}`}>
                                  <input type="checkbox" checked={noteApplyAll} onChange={(e) => setNoteApplyAll(e.target.checked)} className="accent-[#00305E]" />
                                  Apply this note to all {(goal.objectives || []).length} objectives in this goal
                                </label>
                              )}
                              <div className="flex items-center gap-1">
                                <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} className="h-8 w-36 text-xs" title="Report date (defaults to today)" />
                                <Button size="sm" onClick={() => addNote(o.id)} data-testid={`note-save-${o.id}`}>Add</Button>
                                <Button size="sm" variant="ghost" onClick={() => { setNoteFor(null); setNoteText(""); setNoteDate(""); setNoteApplyAll(false); }}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <button className="text-xs text-[#00305E] hover:underline inline-flex items-center gap-0.5 pt-0.5" onClick={() => { setNoteFor(o.id); setNoteText(""); setNoteDate(""); setNoteApplyAll(false); }} data-testid={`add-note-${o.id}`}><MessageSquarePlus className="w-3 h-3" />Add note</button>
                          )}
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Add objective */}
        {addingObj ? (
          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <Textarea value={newObj.description} onChange={(e) => setNewObj((p) => ({ ...p, description: e.target.value }))} placeholder="Stepped benchmark / objective description" data-testid="new-objective-desc" />
            <div className="flex flex-wrap gap-2">
              <Input value={newObj.progress_status} onChange={(e) => setNewObj((p) => ({ ...p, progress_status: e.target.value }))} placeholder="Current progress (e.g. Progress Made)" className="w-56" />
              <Input value={newObj.measurement} onChange={(e) => setNewObj((p) => ({ ...p, measurement: e.target.value }))} placeholder="Measurement (e.g. Observation)" className="w-56" />
              <Button size="sm" onClick={addObj} data-testid="save-new-objective"><Check className="w-4 h-4 mr-1" />Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setAddingObj(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAddingObj(true)} data-testid={`add-objective-${goal.id}`}><Plus className="w-4 h-4 mr-1" />Add objective</Button>
        )}
        </>)}
      </CardContent>

      <AlertDialog open={confirmDelGoal} onOpenChange={setConfirmDelGoal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this goal?</AlertDialogTitle>
            <AlertDialogDescription>This removes the entire goal, its objectives, and all progress notes. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDeleteGoal} data-testid="confirm-delete-goal">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelObj !== null} onOpenChange={(v) => { if (!v) setConfirmDelObj(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this objective?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this? This removes the objective and its progress notes. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delObj(confirmDelObj)} data-testid="confirm-delete-objective">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRoll} onOpenChange={setConfirmRoll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll this goal over?</AlertDialogTitle>
            <AlertDialogDescription>A fresh copy is created for the next school year, carrying the full note history. This goal will be marked "Rolled Over" and kept as history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRollover} data-testid="confirm-rollover">Roll Over</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid={`template-dialog-${goal.id}`}>
          <DialogHeader>
            <DialogTitle>Save goal as template</DialogTitle>
            <DialogDescription>Saves this goal's type, statement, and objectives (without progress notes) as a reusable template for new goals.</DialogDescription>
          </DialogHeader>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" data-testid={`template-name-${goal.id}`} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplOpen(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} data-testid={`template-save-${goal.id}`}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
